import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, ArrowRight, Sparkles } from 'lucide-react';
import * as vendorApi from '../api/vendor.api';

// "Step X of 5" progress banner for clinics still completing their
// onboarding. Renders nothing once the profile is fully complete.
//
// The list of steps + completion checks come from the server
// (GET /vendor/onboarding-progress) so backend + UI stay in sync.
function OnboardingProgress({ onProgress }) {
  const [state, setState] = useState(null);

  useEffect(() => {
    let cancelled = false;
    vendorApi.getOnboardingProgress()
      .then((r) => { if (!cancelled) { setState(r); onProgress?.(r); } })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!state) return null;
  if (state.isComplete) {
    // Full completion — only show a small confirmation banner if the
    // clinic is still pending admin approval.
    if (state.approvalStatus !== 'APPROVED') {
      return (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 14px', margin: '0 0 var(--s-4) 0',
          background: '#ecfdf5', border: '1px solid #a7f3d0',
          borderRadius: 10, fontSize: 13, color: '#065f46',
        }}>
          <CheckCircle2 size={16} />
          <strong>Onboarding complete.</strong>
          <span>Your submission is with our super-admin for review.</span>
        </div>
      );
    }
    return null;
  }

  const pct = Math.round((state.completed / state.total) * 100);
  const currentStep = state.steps[state.current - 1];

  return (
    <div
      data-tour="onboarding-progress"
      style={{
        margin: '0 0 var(--s-4) 0',
        padding: '16px 20px',
        background: 'linear-gradient(135deg, #eef4ff 0%, #dbeafe 100%)',
        border: '1px solid #bfdbfe',
        borderRadius: 12,
        color: '#0f172a',
      }}
    >
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Sparkles size={16} color="#1d4ed8" />
            <strong style={{ fontSize: 14 }}>
              Step {state.current} of {state.total}: {currentStep?.label || 'Next step'}
            </strong>
          </div>
          <div style={{ fontSize: 13, color: '#334155', marginBottom: 12 }}>
            Complete your clinic profile so our super-admin can review and approve your onboarding.
          </div>
        </div>

        <Link
          to="/clinic-profile"
          data-tour="onboarding-cta"
          className="btn btn-primary btn-sm"
          style={{ whiteSpace: 'nowrap' }}
        >
          Complete now <ArrowRight size={14} />
        </Link>
      </div>

      {/* Progress bar */}
      <div style={{
        marginTop: 4,
        height: 6,
        background: '#ffffff',
        border: '1px solid #bfdbfe',
        borderRadius: 999,
        overflow: 'hidden',
      }}>
        <div style={{
          width: `${pct}%`,
          height: '100%',
          background: 'linear-gradient(90deg,#0ea5a4,#0284c7)',
          transition: 'width 400ms ease',
        }} />
      </div>

      {/* Step chips */}
      <div style={{
        marginTop: 12,
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap',
      }}>
        {state.steps.map((s, i) => {
          const done = s.complete;
          const isCurrent = state.current - 1 === i;
          return (
            <div
              key={s.id}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                border: `1px solid ${done ? '#a7f3d0' : isCurrent ? '#93c5fd' : '#e2e8f0'}`,
                background: done ? '#ecfdf5' : isCurrent ? '#ffffff' : '#f8fafc',
                borderRadius: 999,
                fontSize: 12,
                color: done ? '#065f46' : isCurrent ? '#1d4ed8' : '#64748b',
                fontWeight: isCurrent ? 700 : 500,
              }}
            >
              {done
                ? <CheckCircle2 size={12} />
                : <Circle size={10} strokeWidth={2.5} />}
              <span>{s.label}</span>
              {s.optional && !done && (
                <span style={{ marginLeft: 4, fontSize: 10, color: '#94a3b8' }}>(optional)</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default OnboardingProgress;
